import React, { useState } from "react";
import { MapPin, Bed, Bath, Car, Edit, Share2, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { formatMelbourneDayLabel } from "../../../utils/melbourneTime";
import LazyImage from "../../../components/common/LazyImage";
import { formatShortAddress } from "../../../utils/addressFormatter";
import useSwipe from "../../../hooks/useSwipe";

// Format price helper
const formatPrice = (price) => {
    if (!price) return null;
    const num = Number(price);
    if (isNaN(num)) return price;
    return `$${Math.round(num).toLocaleString()}`;
};

// Get price type label from price_display
const getPriceTypeLabel = (priceDisplay) => {
    const labels = {
        'exact': 'Asking Price',
        'range': 'Price Range',
        'contact': 'Contact Agent',
        'auction': 'Guided Price'
    };
    return labels[priceDisplay] || 'Asking Price';
};

const PropertyCard = ({
    property,
    daysOnMarket,
    basePath = '/property',
    onEdit
}) => {
    const navigate = useNavigate();
    // Sale price, yield stats, and Method of Sale are sales-only concepts - a
    // rental has no sale price, so they only ever render "$0" / "N/A" clutter
    // on the tile.
    const isRental = property?.transaction_type === 'lease';
    const imagesArr = property?.images?.map((img) => img.file || img).filter(Boolean) || [];
    const hasMultipleImages = imagesArr.length > 1;
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const displayImage = imagesArr.length > 0 ? imagesArr[currentImageIndex] : null;

    const handlePrevImage = (e) => {
        if (e) e.stopPropagation();
        setCurrentImageIndex(prev => prev === 0 ? imagesArr.length - 1 : prev - 1);
    };

    const handleNextImage = (e) => {
        if (e) e.stopPropagation();
        setCurrentImageIndex(prev => prev === imagesArr.length - 1 ? 0 : prev + 1);
    };

    // Swipe handlers for mobile touch navigation
    const swipeHandlers = useSwipe({
        onSwipeLeft: () => hasMultipleImages && handleNextImage(),
        onSwipeRight: () => hasMultipleImages && handlePrevImage(),
        minSwipeDistance: 50,
    });

    // Calculate price display with price type label
    const getPriceDisplay = () => {
        // Freeform price display text takes precedence over price/price_to
        // formatting — mirrors the Buyer Portal so the CRM shows the same value.
        const freeform = (property.price_display_text || '').trim();
        if (freeform) {
            return freeform;
        }

        const priceDisplay = property.price_display || 'exact';
        const priceTypeLabel = getPriceTypeLabel(priceDisplay);

        // Get price value (check both price and price_from)
        const priceValue = property.price || property.price_from;

        // Price Range: Show "value-value" (label intentionally omitted)
        if (priceDisplay === 'range') {
            const priceTo = property.price_to;
            if (priceValue && priceTo && Number(priceTo) !== Number(priceValue)) {
                return `${formatPrice(priceValue)} – ${formatPrice(priceTo)}`;
            } else if (priceValue) {
                return `${formatPrice(priceValue)}`;
            }
            return '';
        }

        // Contact Agent: Show only "Contact Agent" (no value)
        if (priceDisplay === 'contact') {
            return `${priceTypeLabel}`;
        }

        // Auction: Show "Guided Price: value" if price exists
        if (priceDisplay === 'auction') {
            if (priceValue) {
                return `${priceTypeLabel}: ${formatPrice(priceValue)}`;
            }
            return `${priceTypeLabel}`;
        }

        // Asking Price (exact): Show just the value (label intentionally omitted)
        if (priceDisplay === 'exact') {
            if (priceValue) {
                return `${formatPrice(priceValue)}`;
            }
            return `Contact Agent`;
        }

        // Fallback
        if (!priceValue) {
            return `${priceTypeLabel}: Contact Agent`;
        }

        return `${priceTypeLabel}: ${formatPrice(priceValue)}`;
    };

    // Where the card points - a CRM Property reached via the Collings Properties
    // list still has a detailPath of /properties/<id>, so basePath is only the
    // "where to return to" signal.
    const detailTo = property.detailPath || `${basePath}/${property.id}`;

    return (
        <div
            className="bg-white rounded-[10px] overflow-hidden cursor-pointer border border-transparent hover:shadow-lg transition-all duration-300 flex flex-col group relative h-full"
            {...swipeHandlers}
        >
            {/* Stretched link makes the WHOLE card a real anchor: right-click
                "Open link in new tab" and Ctrl/Cmd+click open the property
                profile, and right-clicking the photo opens the property, not the
                image (Michael Piccoli, DOMA 13225498426). Interactive controls
                (carousel arrows, edit) sit above it via z-10; a plain click still
                navigates in-app. `state` rides along only on an in-app click - a
                new tab loads fresh and the detail page fetches by id. */}
            <Link
                to={detailTo}
                state={{ property, from: basePath }}
                aria-label={`Open ${formatShortAddress(property)}`}
                className="absolute inset-0 z-[1]"
            />
            {/* Image Section */}
            <div
                className="relative aspect-[16/10] overflow-hidden group/image touch-pan-y"
            >
                {displayImage ? (
                    <LazyImage
                        src={displayImage}
                        alt={property.title || "Property"}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400">
                        No Image Available
                    </div>
                )}

                {/* Image Navigation Arrows */}
                {hasMultipleImages && (
                    <>
                        <button
                            onClick={handlePrevImage}
                            className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 bg-black/30 hover:bg-black/50 rounded-full flex items-center justify-center text-white transition-all sm:opacity-0 sm:group-hover:opacity-100 z-10"
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <button
                            onClick={handleNextImage}
                            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 bg-black/30 hover:bg-black/50 rounded-full flex items-center justify-center text-white transition-all sm:opacity-0 sm:group-hover:opacity-100 z-10"
                        >
                            <ChevronRight size={16} />
                        </button>
                        {/* Image Dots Indicator */}
                        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-1 z-10">
                            {imagesArr.slice(0, 5).map((_, idx) => (
                                <div
                                    key={idx}
                                    className={`w-1.5 h-1.5 rounded-full transition-all ${idx === currentImageIndex ? 'bg-white' : 'bg-white/50'}`}
                                />
                            ))}
                            {imagesArr.length > 5 && <span className="text-white text-[10px] ml-1">+{imagesArr.length - 5}</span>}
                        </div>
                    </>
                )}

                {/* Status Badges - Top Left */}
                <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                    {/* Market Status Badge (Off Market / Appraisal / On Market) */}
                    {(() => {
                        const statusName =
                            property?.status?.name ||
                            property?.status ||
                            property?.extra_info?.status ||
                            "";
                        const statusLower = typeof statusName === "string" ? statusName.toLowerCase() : "";
                        const isOffMarket = statusLower.includes("off market");
                        const isAppraisal = statusLower.includes("appraisal");
                        // "Listed" is an exact status (not a substring of On/Off
                        // Market Listing) — the won-but-not-yet-uploaded stage.
                        const isListed = statusLower === "listed";
                        const isSold = property.is_sold || statusLower === 'sold';

                        if (isSold) {
                            return null;
                        }
                        if (isListed) {
                            return (
                                <div className="bg-green-600 text-white px-3 py-2 rounded-md text-[13px] font-bold uppercase tracking-widest backdrop-blur-sm shadow-lg shadow-green-600/30">
                                    Listed - awaiting upload
                                </div>
                            );
                        }
                        if (isOffMarket) {
                            return (
                                <div className="bg-[#1a1a1a]/80 text-white px-3 py-2 rounded-md text-[13px] font-bold uppercase tracking-widest backdrop-blur-sm">
                                    Off Market Listing
                                </div>
                            );
                        }
                        if (isAppraisal) {
                            return (
                                <div className="bg-[#1a1a1a]/80 text-white px-3 py-2 rounded-md text-[13px] font-bold uppercase tracking-widest backdrop-blur-sm">
                                    Appraisal
                                </div>
                            );
                        }
                        return (
                            <div className="bg-[#1a1a1a]/80 text-white px-3 py-2 rounded-md text-[13px] font-bold uppercase tracking-widest backdrop-blur-sm">
                                On Market Listing
                            </div>
                        );
                    })()}
                    {/* Sold Badge - Show first if sold (rentals read "Leased") */}
                    {(property.is_sold || property?.status === 'Sold' || property?.status?.name === 'Sold') && (
                        <div className="bg-green-600 text-white px-2.5 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-widest shadow-lg shadow-green-600/30">
                            {property?.transaction_type === 'lease' ? 'Leased' : 'Sold'}
                        </div>
                    )}
                    {/* Withdrawn Badge - Show if withdrawn (and not sold, or show both) */}
                    {property.is_withdrawn && (
                        <div className="bg-amber-500 text-white px-2.5 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-widest shadow-lg shadow-amber-500/30">
                            Withdrawn
                        </div>
                    )}
                    {/* Under Contract Badge — visible while is_under_contract is true and the property hasn't been sold (rentals read "Under Application") */}
                    {property.is_under_contract && !(property.is_sold || property?.status === 'Sold' || property?.status?.name === 'Sold') && (
                        <div className="bg-purple-600 text-white px-2.5 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-widest shadow-lg shadow-purple-600/30">
                            {property?.transaction_type === 'lease' ? 'Under Application' : 'Under Contract'}
                        </div>
                    )}
                    {/* Scheduled Badge - Show if launch date is in the future */}
                    {property.listing_launch_at && new Date(property.listing_launch_at) > new Date() && (
                        <div className="bg-[#55C5D0] text-white px-2.5 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-widest shadow-lg shadow-[#55C5D0]/30 animate-pulse">
                            Scheduled
                        </div>
                    )}
                </div>

                {/* Action Buttons Overlay */}
                <div className="absolute top-3 right-3 flex gap-2 z-10">
                    {/* Edit Button */}
                    <button
                        className="w-8 h-8 bg-white/95 hover:bg-white rounded-full flex items-center justify-center transition-colors shadow-sm text-[#6B7280] hover:text-[#55C5D0]"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onEdit) {
                                onEdit(property);
                            } else {
                                navigate(`/properties/edit/${property.id}`);
                            }
                        }}
                        title="Edit Property"
                    >
                        <Edit size={16} />
                    </button>
                    {/* Delete button hidden per request */}
                </div>
            </div>

            {/* Content Section */}
            <div className="p-5 pt-4 flex flex-col gap-2 flex-1">
                {/* Title/Address — a real link so the card can be right-clicked /
                    cmd-clicked / middle-clicked open in a new tab (Christian
                    Gravias, Monday 13225498426). stopPropagation keeps a plain
                    left-click from also firing the card onClick and navigating
                    twice. */}
                <h3 className="text-[18px] font-bold text-[#333333] text-center line-clamp-1 leading-snug">
                    <Link
                        to={property.detailPath || `${basePath}/${property.id}`}
                        state={{ property, from: basePath }}
                        onClick={(e) => e.stopPropagation()}
                        className="text-inherit no-underline hover:underline"
                    >
                        {formatShortAddress(property)}
                    </Link>
                </h3>

                {/* Price - same format and Weekly Rent line for Public, My Listings, Collings.
                    A rental has no sale price, so only Weekly Rent applies. */}
                <div className="text-center px-2 min-h-[32px] flex flex-col items-center justify-center">
                    {!isRental && (
                        <p className="text-[14px] sm:text-[19px] font-bold text-[#5F6368] break-words line-clamp-2">
                            {getPriceDisplay()}
                        </p>
                    )}
                    {property?.weekly_rent != null && property.weekly_rent !== '' && (
                        <p className="text-[14px] sm:text-[19px] font-bold text-gray-600 mt-0.5">
                            Weekly Rent: {formatPrice(property.weekly_rent)}
                        </p>
                    )}
                </div>

                {/* Stats */}
                <div className="flex items-center justify-center gap-8 py-0.5">
                    <div className="flex items-center gap-2 text-[#5F6368]">
                        <Bed size={22} strokeWidth={1.5} className="text-[#323D3D]" />
                        <span className="text-[16px] font-medium">{property.beds || 0}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[#5F6368]">
                        <Bath size={22} strokeWidth={1.5} className="text-[#323D3D]" />
                        <span className="text-[16px] font-medium">{property.baths || 0}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[#5F6368]">
                        <Car size={22} strokeWidth={1.5} className="text-[#323D3D]" />
                        <span className="text-[16px] font-medium">{
                            (Number(property.parkings) || 0)
                            + (Number(property.car_ports) || 0)
                            + (Number(property.open_spaces) || 0)
                            || (Number(property.parking) || 0)
                        }</span>
                    </div>
                </div>

                <div className="h-[1px] bg-gray-200 w-full my-1"></div>

                {/* Yield Information - a sales-only concept, not applicable to rentals */}
                {!isRental && (
                    <div className="grid grid-cols-3 gap-1 py-0.5">
                        <div className="flex flex-col items-center">
                            <span className="text-[10px] text-[#5F6368] font-bold capitalize tracking-normal whitespace-nowrap">Est. Gross Yield</span>
                            <span className="text-[18px] font-bold text-[#00A859] mt-1">
                                {property?.est_gross_yield ? `${parseFloat(property.est_gross_yield).toFixed(1)}%` : 'N/A'}
                            </span>
                        </div>
                        <div className="flex flex-col items-center">
                            <span className="text-[10px] text-[#5F6368] font-bold capitalize tracking-normal whitespace-nowrap">Est. Outgoings P.A.</span>
                            <span className="text-[18px] font-bold text-[#2DC8DB] mt-1">
                                {property?.est_outgoings ? `$${Math.round(property.est_outgoings).toLocaleString()}` : 'N/A'}
                            </span>
                        </div>
                        <div className="flex flex-col items-center">
                            <span className="text-[10px] text-[#5F6368] font-bold capitalize tracking-normal whitespace-nowrap">Est. Net Yield</span>
                            <span className="text-[18px] font-bold text-[#2DC8DB] mt-1">
                                {property?.est_net_yield ? `${parseFloat(property.est_net_yield).toFixed(1)}%` : 'N/A'}
                            </span>
                        </div>
                    </div>
                )}

                {/* Method of Sale Footer - show only backend method_of_sale; auction date only when method is Auction.
                    A rental has no method of sale, so label it as a lease instead of defaulting to "Private Sale". */}
                <div className="pt-2 mt-auto border-t border-gray-100">
                    <div className="text-[11px] text-center leading-relaxed text-gray-500 uppercase tracking-wide">
                        {(() => {
                            if (isRental) return 'For Lease';
                            const method = (property.method_of_sale || 'Private Sale').trim();
                            const isAuction = method.toLowerCase() === 'auction';
                            if (isAuction && property.auction_date) {
                                return `AUCTION ${formatMelbourneDayLabel(property.auction_date)}`;
                            }
                            return method;
                        })()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PropertyCard;

